import { useEffect } from "react";

type Options = {
  /** Only listen while the viewer is open. */
  enabled: boolean;
  showNext: boolean;
  showPrevious: boolean;
  onNext: () => void;
  onPrevious: () => void;
};

/**
 * Arrow-key navigation for the attachment viewer (Esc is handled by the Radix
 * Dialog).
 *
 * The viewer is mounted once (in MiddleColumn), so there's a single window
 * listener. It still bails on `defaultPrevented` defensively — so it never
 * hijacks an arrow key another handler already consumed — and calls
 * preventDefault() itself to suppress arrow-key scrolling while navigating.
 */
export default function useViewerKeyboardNav({
  enabled,
  showNext,
  showPrevious,
  onNext,
  onPrevious,
}: Options) {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) {
        return;
      }
      // A focused media element or form control owns its arrow keys (native
      // video seek ±5s, slider steps, caret movement). Those are UA default
      // actions that run AFTER bubbling, so the preventDefault below would
      // cancel them every time — bail instead of hijacking.
      if (
        e.target instanceof Element &&
        e.target.closest(
          "video, audio, input, textarea, select, [contenteditable='true'], [role='slider']",
        )
      ) {
        return;
      }
      if (e.key === "ArrowRight" && showNext) {
        e.preventDefault();
        onNext();
      } else if (e.key === "ArrowLeft" && showPrevious) {
        e.preventDefault();
        onPrevious();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled, showNext, showPrevious, onNext, onPrevious]);
}
